import { useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { ConvoAIErrorMessage, type ConvoAIStreamError } from '@atlassian/ai-agent-streaming';
import type { HookProps } from '@atlassian/ai-work-suggestion/types';

import { fg } from '@confluence/feature-gating';

import { useAiIssueCreateAnalytics } from '../utils/useAiIssueCreateAnalytics';

export const isWhitelistedSLOError = (error: ConvoAIStreamError) => {
	const whitelistedSLOErrors: Array<ConvoAIErrorMessage> = [
		ConvoAIErrorMessage.ACCEPTABLE_USE_VIOLATIONS,
		ConvoAIErrorMessage.NOT_ENOUGH_CONTENT_ERROR,
		ConvoAIErrorMessage.ANSWER_UNAVAILABLE_FOR_CONTENT,
		ConvoAIErrorMessage.NOT_FOUND,
		ConvoAIErrorMessage.BAD_REQUEST,
	];
	return whitelistedSLOErrors.includes(error.message.message_template as ConvoAIErrorMessage);
};

export const useStreamingHookProps = ({
	confluencePage,
	highlightedText,
	overrideXProduct,
}: {
	confluencePage: string;
	highlightedText: string;
	overrideXProduct?: string;
}) => {
	const { fireTrack } = useAiIssueCreateAnalytics();
	const { locale } = useIntl();
	return useMemo(
		() => ({
			analyticsConfig: {
				aiFeatureName: 'confluenceAiWorkCreationConvoAI',
				product: 'confluence',
				subproduct: 'jira',
				useDefaultGASv3EventSchema: false,
				source: 'highlightActionsMenu',
				skipAISessionValidation: true,
			},
			requestHeaderOverrides: {
				overrideXProduct,
			},
			issueConfig: {
				max_issues: 1,
				suggested_issue_field_types: [
					{
						issue_type: 'Task',
						fields: [
							{
								field_name: 'Summary',
								field_type: 'Short-Text',
							},
							{
								field_name: 'Description',
								field_type: 'Paragraph',
							},
						],
					},
				],
			},
			experienceId: 'ai-issue-create-confluence',
			sourceType: 'documentation',
			input: {
				source: 'CONFLUENCE',
				context: {
					primary_text: confluencePage,
					highlighted_text: highlightedText,
				},
				...(fg('aiwc_migrate_to_convo_ai_sends_locale_crcs-2021') && {
					locale,
				}),
			},
			onCompletion: () => {},
			customExperienceTracker: {
				start: () => {
					fg('aiwc_migrate_to_convo_ai_sends_locale_crcs-2021')
						? fireTrack('aiIssueCreateFetchSuggestedFieldsRequest start', {
								locale,
							})
						: fireTrack('aiIssueCreateFetchSuggestedFieldsRequest start');
				},
				succeed: () => {
					fg('aiwc_migrate_to_convo_ai_sends_locale_crcs-2021')
						? fireTrack('aiIssueCreateFetchSuggestedFieldsRequest complete', {
								haveResult: true,
								locale,
							})
						: fireTrack('aiIssueCreateFetchSuggestedFieldsRequest complete', {
								haveResult: true,
							});
				},
				fail: (convoAIStreamError: ConvoAIStreamError) => {
					fg('aiwc_migrate_to_convo_ai_sends_locale_crcs-2021')
						? fireTrack('aiIssueCreateFetchSuggestedFieldsRequest fail', {
								errorMessage: convoAIStreamError?.message?.content,
								statusCode: convoAIStreamError?.message?.status_code,
								message_template: convoAIStreamError?.message?.message_template,
								shouldSlo: !isWhitelistedSLOError(convoAIStreamError),
								locale,
							})
						: fireTrack('aiIssueCreateFetchSuggestedFieldsRequest fail', {
								errorMessage: convoAIStreamError?.message?.content,
								statusCode: convoAIStreamError?.message?.status_code,
								message_template: convoAIStreamError?.message?.message_template,
								shouldSlo: !isWhitelistedSLOError(convoAIStreamError),
							});
				},
				abort: () => {
					fg('aiwc_migrate_to_convo_ai_sends_locale_crcs-2021')
						? fireTrack('aiIssueCreateFetchSuggestedFieldsRequest abort', {
								locale,
							})
						: fireTrack('aiIssueCreateFetchSuggestedFieldsRequest abort');
				},
			},
		}),
		[confluencePage, highlightedText, overrideXProduct, fireTrack, locale],
	) as HookProps;
};
